/**
 * Responsive design utilities for wizard components
 */

import React from 'react';

/**
 * Breakpoint definitions (matching Tailwind CSS)
 */
export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

/**
 * Get current breakpoint
 */
export const getCurrentBreakpoint = () => {
  const width = window.innerWidth;

  if (width >= breakpoints['2xl']) return '2xl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
};

/**
 * Check if current viewport matches breakpoint
 */
export const isBreakpoint = (breakpoint) => {
  const width = window.innerWidth;

  switch (breakpoint) {
    case 'xs':
      return width < breakpoints.sm;
    case 'sm':
      return width >= breakpoints.sm && width < breakpoints.md;
    case 'md':
      return width >= breakpoints.md && width < breakpoints.lg;
    case 'lg':
      return width >= breakpoints.lg && width < breakpoints.xl;
    case 'xl':
      return width >= breakpoints.xl && width < breakpoints['2xl'];
    case '2xl':
      return width >= breakpoints['2xl'];
    default:
      return false;
  }
};

/**
 * Check if viewport is at least the specified breakpoint
 */
export const isAtLeastBreakpoint = (breakpoint) => {
  const width = window.innerWidth;
  return width >= (breakpoints[breakpoint] || 0);
};

/**
 * Check if viewport is mobile (below md breakpoint)
 */
export const isMobile = () => {
  return window.innerWidth < breakpoints.md;
};

/**
 * Check if viewport is tablet (md to lg)
 */
export const isTablet = () => {
  const width = window.innerWidth;
  return width >= breakpoints.md && width < breakpoints.lg;
};

/**
 * Check if viewport is desktop (lg and above)
 */
export const isDesktop = () => {
  return window.innerWidth >= breakpoints.lg;
};

/**
 * Get responsive grid classes based on content count
 */
export const getResponsiveGridClasses = (itemCount, options = {}) => {
  const {
    minCols = 1,
    maxCols = 4,
    gapSize = 4
  } = options;

  // Determine optimal columns based on item count and viewport
  const currentBp = getCurrentBreakpoint();
  let cols;

  switch (currentBp) {
    case 'xs':
    case 'sm':
      cols = Math.min(itemCount, 1);
      break;
    case 'md':
      cols = Math.min(itemCount, 2);
      break;
    case 'lg':
      cols = Math.min(itemCount, 3);
      break;
    case 'xl':
    case '2xl':
      cols = Math.min(itemCount, maxCols);
      break;
    default:
      cols = minCols;
  }

  cols = Math.max(cols, minCols);

  return `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(cols, 3)} xl:grid-cols-${cols} gap-${gapSize}`;
};

/**
 * Get responsive text size classes
 */
export const getResponsiveTextClasses = (size = 'base') => {
  const sizeMap = {
    xs: 'text-xs sm:text-sm',
    sm: 'text-sm sm:text-base',
    base: 'text-base sm:text-lg',
    lg: 'text-lg sm:text-xl',
    xl: 'text-xl sm:text-2xl',
    '2xl': 'text-2xl sm:text-3xl',
    '3xl': 'text-3xl sm:text-4xl'
  };

  return sizeMap[size] || sizeMap.base;
};

/**
 * Get responsive spacing classes
 */
export const getResponsiveSpacing = (size = 'md') => {
  const spacingMap = {
    xs: 'p-2 sm:p-3',
    sm: 'p-3 sm:p-4',
    md: 'p-4 sm:p-6',
    lg: 'p-6 sm:p-8',
    xl: 'p-8 sm:p-10'
  };

  return spacingMap[size] || spacingMap.md;
};

/**
 * Get responsive button classes
 */
export const getResponsiveButtonClasses = (size = 'md') => {
  const buttonMap = {
    sm: 'px-3 py-2 text-sm sm:px-4 sm:py-2',
    md: 'px-4 py-2 text-base sm:px-6 sm:py-3',
    lg: 'px-6 py-3 text-lg sm:px-8 sm:py-4'
  };

  return buttonMap[size] || buttonMap.md;
};

/**
 * Get responsive form input classes
 */
export const getResponsiveInputClasses = () => {
  return 'px-3 py-2 text-sm sm:px-4 sm:py-3 sm:text-base';
};

/**
 * Get responsive modal classes
 */
export const getResponsiveModalClasses = () => {
  return 'w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-4 sm:mx-auto';
};

/**
 * Get responsive wizard layout classes
 */
export const getWizardLayoutClasses = () => {
  return {
    container: 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8',
    content: 'space-y-6 sm:space-y-8',
    section: 'rounded-lg p-4 sm:p-6',
    grid: 'grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6',
    actions: 'flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 sm:space-x-4'
  };
};

/**
 * Get responsive template grid classes
 */
export const getTemplateGridClasses = (templateCount) => {
  if (templateCount <= 2) {
    return 'grid grid-cols-1 sm:grid-cols-2 gap-4';
  } else if (templateCount <= 4) {
    return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4';
  } else {
    return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';
  }
};

/**
 * Get responsive voice settings layout
 */
export const getVoiceSettingsLayout = () => {
  return {
    container: 'space-y-6',
    grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6',
    buttonGroup: 'space-y-2'
  };
};

/**
 * Get responsive chat interface classes
 */
export const getChatInterfaceClasses = () => {
  return {
    container: 'flex flex-col h-96 sm:h-[500px]',
    messages: 'flex-1 overflow-y-auto p-4 sm:p-6 space-y-4',
    input: 'border-t p-4 sm:p-6',
    inputField: 'w-full px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base',
    sendButton: 'px-4 py-2 sm:px-6 sm:py-3'
  };
};

/**
 * Hook for responsive breakpoint detection
 */
export const useResponsive = () => {
  const [breakpoint, setBreakpoint] = React.useState(getCurrentBreakpoint());
  const [isMobileView, setIsMobileView] = React.useState(isMobile());

  React.useEffect(() => {
    const handleResize = () => {
      setBreakpoint(getCurrentBreakpoint());
      setIsMobileView(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    breakpoint,
    isMobile: isMobileView,
    isTablet: isTablet(),
    isDesktop: isDesktop(),
    isAtLeast: isAtLeastBreakpoint
  };
};

/**
 * Get touch-friendly classes for mobile
 */
export const getTouchFriendlyClasses = () => {
  return {
    button: 'min-h-[44px] min-w-[44px]', // Apple's recommended minimum touch target
    input: 'min-h-[44px]',
    clickable: 'min-h-[44px] min-w-[44px] flex items-center justify-center'
  };
};

/**
 * Get responsive wizard progress indicator classes
 */
export const getProgressIndicatorClasses = () => {
  return {
    desktop: 'hidden md:block',
    mobile: 'md:hidden',
    step: 'flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10',
    connector: 'flex-1 h-1 mx-2 sm:mx-4',
    label: 'text-xs sm:text-sm mt-2 sm:mt-3'
  };
};

export default {
  breakpoints,
  getCurrentBreakpoint,
  isBreakpoint,
  isAtLeastBreakpoint,
  isMobile,
  isTablet,
  isDesktop,
  getResponsiveGridClasses,
  getResponsiveTextClasses,
  getResponsiveSpacing,
  getResponsiveButtonClasses,
  getResponsiveInputClasses,
  getResponsiveModalClasses,
  getWizardLayoutClasses,
  getTemplateGridClasses,
  getVoiceSettingsLayout,
  getChatInterfaceClasses,
  getTouchFriendlyClasses,
  getProgressIndicatorClasses
};
